/**
 * ACTIVE 校区选项加载 hook：一次拉取全量校区（后端已按 campusScope 数据范围过滤，
 * 校区级账号仅返回授权校区），前端过滤 ACTIVE 供顶栏切换器与页面表单共用。
 * 加载失败置空列表（切换器无校区选项、持久化值校验按空集合 fail-closed 回落 'ALL'）。
 * 对齐 admin-global-campus-switch:SC-08（选项口径）与 design D7。
 */
import { useEffect, useState } from 'react';
import { orgCampusApi, type CampusItem } from '../api/campuses.api';
import { sortCampusesByNumericCode } from '../utils/campus-sort';

/** useCampusOptions 返回值。 */
export interface CampusOptionsResult {
  /** ACTIVE 校区列表 */
  campuses: CampusItem[];
  /** 首次加载是否进行中 */
  loading: boolean;
}

/** 拉取校区列表并过滤 ACTIVE 后按 numericCode 升序排序（page=1/pageSize=100，对齐各页既有加载口径；排序复用共享工具 sortCampusesByNumericCode，与各表单自拉列表的校区下拉同一事实源）。enabled=false 时不发请求（权限快照就绪前不拉校区，过期会话启动仅发一次鉴权探测）。 */
export function useCampusOptions(enabled = true): CampusOptionsResult {
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    orgCampusApi
      .listCampuses({ page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        setCampuses(
          sortCampusesByNumericCode(res.items.filter((c) => c.status === 'ACTIVE')),
        );
      })
      .catch(() => {
        // 加载失败不重试不弹错：空列表即 fail-closed（切换器无校区项、持久化值校验不通过回落 'ALL'）
        if (!cancelled) setCampuses([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { campuses, loading };
}
